import { RootAdminDashboardContext } from "components/utils/Contexts";
import { getProgressBarColor } from "components/main-view/utils/functions/progressUtils";
import React, { useContext, useRef, useState } from "react";
import {
  MdAlternateEmail,
  MdMoreHoriz,
  MdOutlineContacts,
  MdOutlineHowToReg,
  MdOutlineLocationOn,
  MdOutlinePhoneAndroid,
} from "react-icons/md";
import { externalNavigate, formatPhoneNumber } from "utils/utils";
import { BACKEND_URL } from "config/getUrl";
import { BrandListType } from "utils/interfaces";
import { usePlatform } from "components/utils/PlatformContext";
import useMouseClickOutside from "hooks/MouseClickOutside";
import EditDeleteMenu from "../components/EditDeleteMenu";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

interface BrandCardProps {
  brand: BrandListType;
}

const BrandCard: React.FC<BrandCardProps> = ({
  brand,
  // handleClickEdit,
  // handleClickDelete,
}) => {
  // States definition
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Create a menu ref
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Get the platform context
  const { logo, studentsTerminology } = usePlatform();

  // Getting the values of the context
  const { version } = useContext(RootAdminDashboardContext);

  /**
   * Routine to calculate the enrolmentRate
   */
  // Get the number of students
  const stundentsEnrolled = 0;
  // school && school.students ? school.students.length : 0;

  // Calculate relevant values
  const enrolmentRate = 0;
  //   school.licenses !== 0
  //     ? Math.round((stundentsEnrolled / school.licenses) * 100)
  //     : 0;

  /**
   * Hook used when clicking outside the dropdown menu
   */
  useMouseClickOutside(
    menuRef,
    () => setShowOptionsMenu(false),
    showOptionsMenu,
  );

  /**
   * Function executed when clicking on card. It redirects
   * to the respective platform
   */
  const handleCardClick = () => {
    externalNavigate(`https://${brand.host}`);
  };

  /**
   * Function used to open the pop up menu with the edit and
   * delete option
   * @param event
   */
  const handleClickOptions = (event: React.MouseEvent) => {
    // First of all, prevent clicking the card
    event.stopPropagation();

    // Toggle the state
    setShowOptionsMenu(!showOptionsMenu);
  };

  /**
   * Function used to wrap the edit or delete functions
   * in order to add custom functionalities per card
   */
  const handleClickOptionWrapper = (
    func: (event: React.MouseEvent) => void,
  ) => {
    return (event: React.MouseEvent) => {
      // Close the menu when clicking on one of the options
      setShowOptionsMenu(false);

      // And execute the function
      func(event);
    };
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="w-full h-48 p-6 bg-neutral-25 rounded-xl justify-between items-start inline-flex cursor-pointer hover:shadow-card ease-in-out duration-200"
      onClick={handleCardClick}
    >
      <div className="w-[190px] self-stretch flex-col justify-between items-start inline-flex">
        <div className="self-stretch text-neutral-700 text-[19px] font-semibold font-sans leading-[23px]">
          {brand.title}
        </div>
        <ImageSkeleton
          src={brand.logo}
          alt=""
          className="w-[48px] h-[48px] rounded-full relative overflow-hidden"
        />
      </div>
      <div className="w-[190px] p-1 rounded flex-col justify-start items-start gap-2 inline-flex">
        <div className="self-stretch h-[75px] flex-col justify-start items-start gap-2 flex">
          <div className="p-2 bg-neutral-50 rounded-full justify-start items-end gap-2 inline-flex">
            <MdOutlineContacts className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch text-neutral-600 text-base font-semibold font-sans leading-[19px] overflow-hidden break-keep whitespace-nowrap text-ellipsis">
            {/* {school.contacts.length > 0 ? school.contacts[0].firstName : "-"}{" "}
            {school.contacts.length > 0 ? school.contacts[0].lastName : "-"} */}
          </div>
        </div>
        <div className="self-stretch h-[38px] flex-col justify-start items-start gap-1.5 flex">
          <div className="self-stretch justify-start items-center gap-1 inline-flex">
            <MdAlternateEmail className="w-4 h-4 fill-neutral-500" />
            <div className="w-[162px] text-start text-neutral-500 text-xs font-normal font-inter leading-[14px] overflow-hidden text-ellipsis">
              {/* {school.contacts.length > 0 ? school.contacts[0].email : "-"} */}
            </div>
          </div>
          <div className=" justify-start items-center gap-1 inline-flex">
            <MdOutlinePhoneAndroid className="w-4 h-4 fill-neutral-500" />
            <div className="text-center text-neutral-500 text-xs font-normal font-inter uppercase leading-[14px] tracking-wide">
              {/* {school.contacts.length > 0
                ? formatPhoneNumber(school.contacts[0].phone)
                : "-"} */}
            </div>
          </div>
        </div>
      </div>
      <div className="w-[190px] p-1 rounded flex-col justify-start items-start gap-3 inline-flex">
        <div className="self-stretch h-[88px] flex-col justify-start items-start gap-2 flex">
          <div className="p-2 bg-neutral-50 rounded-full justify-start items-end gap-2 inline-flex">
            <MdOutlineHowToReg className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch justify-start items-end gap-1 inline-flex">
            <div className="w-fit text-right text-neutral-700 text-lg font-semibold font-sans leading-snug">
              {/* {enrolmentRate}% */}
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 inline-flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-center text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Enrolment
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch h-0.5 bg-neutral-100 flex-col justify-start items-start gap-2 flex">
            <div
              className={`h-0.5 ${getProgressBarColor(
                Math.min(enrolmentRate, 100),
              )}`}
              style={{ width: `${Math.min(enrolmentRate, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex-col justify-start items-start gap-0.5 flex">
          <div className="justify-start items-center gap-1 inline-flex">
            <div className="w-8 text-center text-neutral-500 text-sm font-semibold font-sans leading-[17px]">
              {/* {stundentsEnrolled} */}
            </div>
            <div className="text-center text-neutral-500 text-xs font-normal font-inter uppercase leading-[14px] tracking-wide">
              {studentsTerminology} enrolled
            </div>
          </div>
          <div className="justify-start items-center gap-1 inline-flex">
            <div className="w-8 text-center text-neutral-500 text-sm font-semibold font-sans leading-[17px]">
              {/* {school.licenses || 0} */}
            </div>
            <div className="text-center text-neutral-500 text-xs font-normal font-inter uppercase leading-[14px] tracking-wide">
              Licenses
            </div>
          </div>
        </div>
      </div>
      <div className="w-[190px] p-1 rounded flex-col justify-start items-start gap-3 inline-flex">
        <div className="self-stretch h-[115px] flex-col justify-start items-start gap-2 flex">
          <div className="p-2 bg-neutral-50 rounded-full justify-start items-end gap-2 inline-flex">
            <MdOutlineLocationOn className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch h-[59px] flex-col justify-start items-start gap-1 flex">
            <div className="self-stretch text-neutral-500 text-sm font-normal font-sans leading-[17px] overflow-hidden break-keep whitespace-nowrap text-ellipsis">
              {/* {school.locations.length > 0 ? school.locations[0].address : "-"},{" "} */}
            </div>
            <div className="self-stretch text-neutral-500 text-sm font-normal font-sans leading-[17px]">
              {/* {school.locations.length > 0 ? school.locations[0].city : "-"},{" "} */}
            </div>
            <div className="self-stretch text-neutral-500 text-sm font-normal font-sans leading-[17px]">
              {/* {school.locations.length > 0 ? school.locations[0].state : "-"}{" "} */}
              {/* {school.locations.length > 0 ? school.locations[0].postCode : "-"} */}
            </div>
          </div>
        </div>
      </div>
      <div
        className="w-12 self-stretch justify-center items-center gap-3 inline-flex relative"
        ref={menuRef}
      >
        <button
          className="p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex cursor-pointer hover:bg-neutral-200 ease-in-out duration-150"
          onClick={handleClickOptions}
          title="More options"
        >
          <MdMoreHoriz className="w-6 h-6 fill-neutral-700" />
        </button>
        <EditDeleteMenu
          show={showOptionsMenu}
          // handleEdit={handleClickOptionWrapper(handleClickEdit)}
          // handleDelete={handleClickOptionWrapper(handleClickDelete)}
          handleEdit={() => {}}
          handleDelete={() => {}}
          containerStyle="absolute w-fit top-[100px] right-0 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
        />
      </div>
    </div>
  );
};

export default BrandCard;
